/**
 * Google Translate Helper
 * Ensures Google Translate works properly after login and language changes
 * Works with the DOM error fix to provide a complete solution
 */

class GoogleTranslateHelper {
    private static instance: GoogleTranslateHelper;
    private initCheckInterval: NodeJS.Timeout | null = null;
    private lastLanguageApplied: string | null = null;
    
    private constructor() {
        this.initialize();
    }

    static getInstance(): GoogleTranslateHelper {
        if (!GoogleTranslateHelper.instance) {
            GoogleTranslateHelper.instance = new GoogleTranslateHelper();
        }
        return GoogleTranslateHelper.instance;
    }

    private initialize(): void {
        this.monitorAuthChanges();
        this.monitorLanguageChanges();
        this.ensureTranslationAfterLogin();
    }

    /**
     * Monitor authentication state changes
     */
    private monitorAuthChanges(): void {
        // Watch for login events
        window.addEventListener('storage', (e) => {
            if (e.key === 'authToken' && e.newValue) {
                this.onUserLogin();
            }
        });

        // Also check if user is already logged in
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            this.ensureGoogleTranslateReady();
        }
    }

    /**
     * Monitor language changes
     */
    private monitorLanguageChanges(): void {
        // Watch for language changes in localStorage
        window.addEventListener('storage', (e) => {
            if (e.key === 'selectedLanguage' && e.newValue && e.newValue !== e.oldValue) {
                this.applyLanguageWithRetry(e.newValue);
            }
        });

        // Monitor the Google Translate select element
        this.monitorGoogleTranslateSelect();
    }

    /**
     * Monitor Google Translate select element for changes
     */
    private monitorGoogleTranslateSelect(): void {
        const checkForSelect = setInterval(() => {
            const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (selectElement) {
                // Add change listener if not already added
                if (!selectElement.dataset.listenerAdded) {
                    selectElement.dataset.listenerAdded = 'true';
                    
                    selectElement.addEventListener('change', () => {
                        const selectedLang = selectElement.value;
                        
                        // Don't save if user is actively changing language (let the manager handle it)
                        const isChangingLanguage = sessionStorage.getItem('changingLanguage') === 'true';
                        if (isChangingLanguage) {
                            return;
                        }
                        
                        // Check what the user actually wants
                        const userSelectedLang = localStorage.getItem('selectedLanguage');
                        
                        // If user selected English but Google Translate shows a different language,
                        // don't save the Google Translate value - the user wants English
                        if (userSelectedLang === 'en' && selectedLang !== '' && selectedLang !== 'en') {
                            return;
                        }
                        
                        // Map empty value to 'en' (original language)
                        const langToStore = selectedLang === '' ? 'en' : selectedLang;
                        
                        // Only store if it's different from current saved value
                        const currentSaved = localStorage.getItem('selectedLanguage');
                        if (currentSaved !== langToStore) {
                            localStorage.setItem('selectedLanguage', langToStore);
                            this.lastLanguageApplied = langToStore;
                        }
                        
                        // Allow the translation to proceed
                        this.allowTranslation();
                    });
                }
            }
        }, 1000);

        // Store interval for cleanup
        (window as any).__googleTranslateSelectMonitor = checkForSelect;
    }

    /**
     * Handle user login
     */
    private onUserLogin(): void {
        // Mark that user just logged in
        sessionStorage.setItem('justLoggedIn', 'true');
        
        // Allow translations immediately
        this.allowTranslation();
        
        // Check for saved language preference
        setTimeout(() => {
            const savedLanguage = localStorage.getItem('selectedLanguage');
            if (savedLanguage && savedLanguage !== 'en') {
                this.applyLanguageWithRetry(savedLanguage);
            }
            
            // Clear the login flag after a delay
            setTimeout(() => {
                sessionStorage.removeItem('justLoggedIn');
            }, 10000);
        }, 2000);
    }

    /**
     * Ensure Google Translate is ready after page load
     */
    private ensureGoogleTranslateReady(): void {
        // Wait for page to fully load
        if (document.readyState !== 'complete') {
            window.addEventListener('load', () => {
                this.checkAndApplySavedLanguage();
            });
        } else {
            this.checkAndApplySavedLanguage();
        }
    }

    /**
     * Check and apply saved language preference
     */
    private checkAndApplySavedLanguage(): void {
        // Clear any previous interval
        if (this.initCheckInterval) {
            clearInterval(this.initCheckInterval);
        }

        let attempts = 0;
        const maxAttempts = 20; // 10 seconds

        this.initCheckInterval = setInterval(() => {
            const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            const savedLanguage = localStorage.getItem('selectedLanguage');
            
            // Check if user is actively changing language (don't interfere)
            const isChangingLanguage = sessionStorage.getItem('changingLanguage') === 'true';
            if (isChangingLanguage) {
                clearInterval(this.initCheckInterval!);
                this.initCheckInterval = null;
                return;
            }
            
            if (selectElement && savedLanguage) {
                // Check if language needs to be applied
                // For English, check if select is empty (original language)
                if (savedLanguage === 'en' && selectElement.value !== '') {
                    this.applyLanguageDirectly('en');
                } else if (savedLanguage !== 'en' && selectElement.value !== savedLanguage) {
                    this.applyLanguageDirectly(savedLanguage);
                }
                
                clearInterval(this.initCheckInterval!);
                this.initCheckInterval = null;
            }
            
            attempts++;
            if (attempts >= maxAttempts) {
                clearInterval(this.initCheckInterval!);
                this.initCheckInterval = null;
            }
        }, 500);
    }

    /**
     * Apply language with retry logic
     */
    private applyLanguageWithRetry(language: string, retries: number = 5): void {
        const attemptApply = (retriesLeft: number) => {
            const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            
            if (!selectElement) {
                if (retriesLeft > 0) {
                    setTimeout(() => attemptApply(retriesLeft - 1), 1000);
                }
                return;
            }

            // Apply the language
            this.applyLanguageDirectly(language);
            
            // Verify it was applied
            setTimeout(() => {
                if (selectElement.value !== language && retriesLeft > 0) {
                    attemptApply(retriesLeft - 1);
                } else if (selectElement.value === language) {
                    this.lastLanguageApplied = language;
                }
            }, 500);
        };

        attemptApply(retries);
    }

    /**
     * Apply language directly to Google Translate
     */
    private applyLanguageDirectly(language: string): void {
        const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (!selectElement) return;

        // Allow translation to proceed
        this.allowTranslation();

        // Set the cookie
        if (language === 'en') {
            document.cookie = 'googtrans=/en/en;path=/';
        } else {
            document.cookie = `googtrans=/en/${language};path=/`;
        }

        // Set the select value
        selectElement.value = language;

        // Trigger change event multiple times for reliability
        const event = new Event('change', { bubbles: true, cancelable: true });
        selectElement.dispatchEvent(event);

        // Trigger again after a delay
        setTimeout(() => {
            if (selectElement.value === language) {
                const event2 = new Event('change', { bubbles: true, cancelable: true });
                selectElement.dispatchEvent(event2);
            }
        }, 100);

        // Store the preference
        localStorage.setItem('selectedLanguage', language);
        this.lastLanguageApplied = language;
    }

    /**
     * Allow translations to proceed
     */
    private allowTranslation(): void {
        // Signal to the coordinator that translations should be allowed
        if ((window as any).googleTranslateCoordinator) {
            (window as any).googleTranslateCoordinator.allowTranslation();
        }

        // Remove any blocking flags
        sessionStorage.setItem('allowTranslation', 'true');
        sessionStorage.setItem('allowGoogleTranslate', 'true');
        
        // Only update timestamp if user is not actively changing language
        const isChangingLanguage = sessionStorage.getItem('changingLanguage') === 'true';
        if (!isChangingLanguage) {
            // Mark the time of the language change
            localStorage.setItem('lastLanguageChangeTime', Date.now().toString());
        }
    }

    /**
     * Ensure translation works after login
     */
    private ensureTranslationAfterLogin(): void {
        // Check if we're on a signed-in page
        const checkSignedIn = () => {
            const authToken = localStorage.getItem('authToken');
            const selectedLanguage = localStorage.getItem('selectedLanguage');
            
            if (authToken && selectedLanguage && selectedLanguage !== 'en') {
                // Check if translation is active
                const isTranslated = document.documentElement.classList.contains('translated-ltr') || 
                                   document.documentElement.classList.contains('translated-rtl');
                
                if (!isTranslated) {
                    this.applyLanguageWithRetry(selectedLanguage);
                }
            }
        };

        // Check immediately
        checkSignedIn();

        // Check again after a delay (for SPA navigation)
        setTimeout(checkSignedIn, 3000);
    }

    /**
     * Force refresh translation
     */
    public forceRefreshTranslation(): void {
        const savedLanguage = localStorage.getItem('selectedLanguage');
        if (savedLanguage && savedLanguage !== 'en') {
            this.allowTranslation();
            this.applyLanguageWithRetry(savedLanguage, 3);
        }
    }

    /**
     * Cleanup
     */
    public destroy(): void {
        if (this.initCheckInterval) {
            clearInterval(this.initCheckInterval);
        }
        
        const selectMonitor = (window as any).__googleTranslateSelectMonitor;
        if (selectMonitor) {
            clearInterval(selectMonitor);
        }
    }
}

// Auto-initialize
const helper = GoogleTranslateHelper.getInstance();

// Export for global access
(window as any).googleTranslateHelper = helper;

export default helper;